import axios from 'axios';

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5001/api',
  headers: { 'Content-Type': 'application/json' }
});

// Request interceptor: attach admin token
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('zuttle_admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — on 401, try one soft-refresh of the JWT and retry
// the original request before forcing logout. 403 means the admin is
// authenticated but not permitted — pass it through so callers can surface
// a "not permitted" error. Network errors / 5xx pass through unchanged.
let isRefreshing = false;
let pendingQueue = [];

const processQueue = (token) => {
  pendingQueue.forEach((cb) => cb(token));
  pendingQueue = [];
};

const forceLogout = () => {
  localStorage.removeItem('zuttle_admin_token');
  localStorage.removeItem('zuttle_admin_user');
  if (typeof window !== 'undefined') {
    window.location.reload();
  }
};

API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;
    const status = error?.response?.status;

    if (
      status !== 401 ||
      !originalRequest ||
      originalRequest._retried ||
      originalRequest.url?.includes('/refresh-token') ||
      originalRequest.url?.includes('/login')
    ) {
      return Promise.reject(error);
    }

    originalRequest._retried = true;

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingQueue.push((newToken) => {
          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(API(originalRequest));
          } else {
            reject(error);
          }
        });
      });
    }

    isRefreshing = true;
    try {
      const { data } = await API.post('/admin/refresh-token');
      const newToken = data?.data?.token;
      if (!newToken) {
        processQueue(null);
        forceLogout();
        return Promise.reject(error);
      }
      localStorage.setItem('zuttle_admin_token', newToken);
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      processQueue(newToken);
      return API(originalRequest);
    } catch (refreshErr) {
      processQueue(null);
      forceLogout();
      return Promise.reject(refreshErr);
    } finally {
      isRefreshing = false;
    }
  }
);

export const AdminAuthAPI = {
  login: (credentials) => API.post('/admin/login', credentials),
  refreshToken: () => API.post('/admin/refresh-token'),
  // Server-side logout: bumps tokenVersion, killing all outstanding tokens
  logout: () => API.post('/admin/logout'),
  forgotPassword: (email) => API.post('/admin/forgot-password', { email }),
  resetPassword: (data) => API.post('/admin/reset-password', data),
  getProfile: () => API.get('/admin/profile'),
};

export const AdminDashboardAPI = {
  getStats: () => API.get('/admin/dashboard'),
};

export const AdminCompanyAPI = {
  getAll: (params) => API.get('/admin/companies', { params }),
  getById: (id) => API.get(`/admin/companies/${id}`),
  updateStatus: (id, status, notes) => API.put(`/admin/companies/${id}/status`, { status, notes }),
  // The operator's payout account, masked to the last four digits, and its
  // audit trail (Paystack plan PR 6), with its review, bank letter and
  // Paystack subaccounts (PR 7a). Any admin reads.
  getBankDetails: (id) => API.get(`/admin/companies/${id}/bank-details`),
  // Super admin only (the server answers 403 to anyone else). Each answers
  // the view above as it stands after the action.
  // A bank change sends `{ callConfirmed: true }` (#541).
  approveBankDetails: (id, body) => API.post(`/admin/companies/${id}/bank-details/approve`, body),
  rejectBankLetter: (id, note) => API.post(`/admin/companies/${id}/bank-details/letter/reject`, { note }),
  refuseBankChange: (id, body) => API.post(`/admin/companies/${id}/bank-details/change/refuse`, body),
  createLiveSubaccount: (id, acceptLetter) => API.post(`/admin/companies/${id}/bank-details/live-subaccount`, { acceptLetter }),
  markSubaccountVerified: (id, env) => API.post(`/admin/companies/${id}/bank-details/subaccounts/${env}/verified`),
  clearUnknownCreate: (id, env, note) => API.post(`/admin/companies/${id}/bank-details/subaccounts/${env}/clear`, { note }),
  // The company's commission (issue #370). Any admin reads; a change is super
  // admin only and answers the view after it.
  getCommission: (id) => API.get(`/admin/companies/${id}/commission`),
  updateCommission: (id, body) => API.put(`/admin/companies/${id}/commission`, body),
};

export const AdminDriverAPI = {
  getAll: (params) => API.get('/admin/drivers', { params }),
  getPending: (params) => API.get('/admin/drivers/pending', { params }),
  updateApproval: (id, approvalStatus, notes) =>
    API.put(`/admin/drivers/${id}/approval`, { approvalStatus, notes }),
  deactivate: (id, notes) => API.put(`/admin/drivers/${id}/deactivate`, { notes }),
  reactivate: (id, notes) => API.put(`/admin/drivers/${id}/reactivate`, { notes }),
  delete: (id, notes) => API.delete(`/admin/drivers/${id}`, { data: { notes } }),
};

export const AdminVehicleAPI = {
  getAll: (params) => API.get('/admin/vehicles', { params }),
  getPending: (params) => API.get('/admin/vehicles/pending', { params }),
  updateApproval: (id, approvalStatus, notes) =>
    API.put(`/admin/vehicles/${id}/approval`, { approvalStatus, notes }),
  deactivate: (id, notes) => API.put(`/admin/vehicles/${id}/deactivate`, { notes }),
  reactivate: (id, notes) => API.put(`/admin/vehicles/${id}/reactivate`, { notes }),
  delete: (id, notes) => API.delete(`/admin/vehicles/${id}`, { data: { notes } }),
};

export const AdminPayoutAPI = {
  getPending: (params) => API.get('/admin/payouts/pending', { params }),
  getSummary: () => API.get('/admin/payouts/summary'),
  exportPayouts: (payoutIds) => API.post('/admin/payouts/export', { payoutIds }),
  process: (id) => API.post(`/admin/payouts/${id}/process`),
  complete: (id, externalReference) => API.post(`/admin/payouts/${id}/complete`, { externalReference }),
  fail: (id, reason) => API.post(`/admin/payouts/${id}/fail`, { reason }),
  // Weekly payouts dashboard
  getWeekly: (weekStart) => API.get('/admin/payouts/weekly', { params: { weekStart } }),
  // `expectedPaidCents` (#553): the paid total the screen showed, 0 for an
  // unpaid week. The server answers 409 PAYOUT_CHANGED when the week changed since.
  markCompanyPaid: (companyId, weekStart, amount, reference, expectedPaidCents) =>
    API.post('/admin/payouts/weekly/mark-paid', { companyId, weekStart, amount, reference, expectedPaidCents }),
  unmarkCompanyPaid: (companyId, weekStart, expectedPaidCents) =>
    API.post('/admin/payouts/weekly/unmark-paid', { companyId, weekStart, expectedPaidCents }),
  weeklyExport: (weekStart) => API.get('/admin/payouts/weekly/export', { params: { weekStart }, responseType: 'blob' }),
};

// Operator receivables (Paystack plan 5.8.2, 8.5, PR 14): read only, any admin.
// params: { status, reason, lane, company, page, limit }; status defaults to open.
export const AdminReceivableAPI = {
  list: (params) => API.get('/admin/receivables', { params }),
};

export const AdminUserAPI = {
  getAll: () => API.get('/admin/users'),
  create: (data) => API.post('/admin/users', data),
  update: (id, data) => API.put(`/admin/users/${id}`, data),
  delete: (id) => API.delete(`/admin/users/${id}`),
  changePassword: (data) => API.put('/admin/change-password', data),
};

// Charter (spec 6.4). Every route is open to any admin except refund and
// cancel, which the server keeps to super admins.
export const AdminCharterAPI = {
  // The client flag: the charter pages show when enabled || hasHires (R77).
  getEnabled: () => API.get('/admin/charter/enabled'),
  listDocuments: (status, companyId) => API.get('/admin/charter/documents', { params: { status, companyId } }),
  reviewDocument: (id, body) => API.put(`/admin/charter/documents/${id}`, body),
  getReadiness: (companyId) => API.get(`/admin/companies/${companyId}/charter-readiness`),
  decide: (companyId, decision, note) => API.put(`/admin/companies/${companyId}/charter`, { decision, note }),
};

export const AdminHireAPI = {
  list: (params) => API.get('/admin/hires', { params }),
  get: (id) => API.get(`/admin/hires/${id}`),
  refund: (id, body) => API.post(`/admin/hires/${id}/refund`, body),
  cancel: (id, body) => API.post(`/admin/hires/${id}/cancel`, body),
};

// Payments to resolve, "Resolve payment" and disputes (Paystack plan 3.7.9,
// 5.9, PR 13). Any admin reads; the server keeps resolve to super admins.
export const AdminPaymentsAPI = {
  toResolve: () => API.get('/admin/payments/to-resolve'),
  resolve: (body) => API.post('/admin/payments/resolve', body),
  disputes: (all = false) => API.get('/admin/payments/disputes', { params: all ? { all: 'true' } : {} }),
};

// Waitlist signups + questionnaire analytics. Read-only.
export const AdminWaitlistAPI = {
  list: (params) => API.get('/admin/waitlist', { params }),
  getStats: () => API.get('/admin/waitlist/stats'),
  demoRequests: () => API.get('/admin/waitlist/demo-requests'),
};

/**
 * A document's signed link as an absolute URL. Without R2 the server returns
 * a path on the API host (`/api/charter-files/...`); with R2 it is already a
 * full presigned URL.
 */
export const charterFileHref = (fileUrl) => {
  if (!fileUrl) return null;
  if (/^https?:\/\//i.test(fileUrl)) return fileUrl;
  const base = (API.defaults.baseURL || '').replace(/\/api\/?$/, '');
  return `${base}${fileUrl}`;
};

export const APIUtils = {
  clearAuth: () => {
    localStorage.removeItem('zuttle_admin_token');
    localStorage.removeItem('zuttle_admin_user');
  },
  handleError: (error) => {
    return error.response?.data?.message || error.message || 'An error occurred';
  }
};

export default API;
